import React from 'react';

const DEFAULT_COLLAPSED_HEIGHT = 150;

// Supports function content (render prop) so callers can inject isExpanded/toggle into JSX.
const renderContent = (content, context) => {
  const resolved = typeof content === 'function' ? content(context) : content;
  if (!resolved) return null;
  if (Array.isArray(resolved))
    return resolved.map((item, i) => (
      <React.Fragment key={i}>{renderContent(item, context)}</React.Fragment>
    ));
  if (typeof resolved === 'string') return <p>{resolved}</p>;
  return resolved;
};

// Compiled once at module load; matches CSS length strings like '70vh', '400px', '50%'.
const HEIGHT_RE = /^(\d+(?:\.\d+)?)(px|vh|%)$/;

// Converts expandedHeight to pixels using the #root scroll container height as the viewport.
const resolveExpandedPx = (expandedHeight, footerHeight) => {
  const root = document.getElementById('root');
  const vh = root ? root.clientHeight : window.innerHeight;
  if (expandedHeight === 'screen') return vh - footerHeight;
  if (typeof expandedHeight === 'number') return expandedHeight;
  const m = String(expandedHeight).match(HEIGHT_RE);
  if (m) {
    const v = parseFloat(m[1]);
    return m[2] === 'px' ? v : (v / 100) * vh;
  }
  return parseInt(expandedHeight, 10) || 500;
};

// ISI height is proportional to scroll progress through the page (0% scroll = collapsed,
// 100% scroll = fully expanded). No threshold — ISI responds to every scroll pixel.
//
// footerRef receives a wheel-event forwarder so desktop mouse-wheel scroll over the
// fixed ISI panel always reaches the #root scroll container instead of being eaten
// by the fixed stacking context.
const useScrollLinked = ({ enabled, collapsedHeight, expandedHeight, footerHeight, spacerExtra, footerOnlyWhenExpanded }) => {
  const panelRef = React.useRef(null);
  const footerRef = React.useRef(null);
  // Each flag is tracked as both a ref and state: the ref gives event handlers the latest
  // value without stale closures; the state triggers React re-renders for UI (e.g. overflowY).
  const expandedRef = React.useRef(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isFullyExpandedRef = React.useRef(false);
  const [isFullyExpanded, setIsFullyExpanded] = React.useState(false);
  // Natural ISI content height (text + footer). Used to cap expansion when
  // footerOnlyWhenExpanded is true so the panel never grows past the actual content.
  // Stored as STATE (not a ref) and set synchronously in useLayoutEffect below, so the
  // spacer height is correct on the FIRST paint and never changes afterward. If it were
  // a ref, the spacer would render with the uncapped (Infinity → viewport) value until
  // the first scroll-triggered re-render, then shrink mid-scroll when the cap kicked in.
  // Chrome hides that document-height change via scroll anchoring; Safari has no scroll
  // anchoring, so it surfaces as extra white space below the footer.
  const [contentNaturalH, setContentNaturalH] = React.useState(Infinity);

  const getExpandedPx = React.useCallback(() => {
    const base = resolveExpandedPx(expandedHeight, footerHeight);
    if (footerOnlyWhenExpanded && contentNaturalH < Infinity) {
      return Math.min(base, contentNaturalH);
    }
    return base;
  }, [expandedHeight, footerHeight, footerOnlyWhenExpanded, contentNaturalH]);

  // Set initial height synchronously before paint so ISI never flashes open.
  // Also capture the content's natural height: with the panel at collapsedHeight,
  // the content div (flex: 1) is 150px tall but its scrollHeight reveals the full
  // unconstrained content height (ISI text + footer).
  React.useLayoutEffect(() => {
    if (!enabled) return;
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.height = `${collapsedHeight}px`;
    panel.style.transition = 'none';
    if (footerOnlyWhenExpanded) {
      const content = panel.querySelector('.scrollable-isi-content');
      // setState in useLayoutEffect re-renders synchronously before paint, so the
      // spacer is sized from the capped height on the first frame the user sees.
      if (content) setContentNaturalH(content.scrollHeight);
    }
  }, [enabled, collapsedHeight, footerOnlyWhenExpanded]);

  // Scroll listener: ISI expands in the last (expandedPx - collapsedHeight) px of scroll —
  // the expansion zone that the spacer reserves. Content above the spacer is always fully
  // visible before the zone begins; the submit/cancel buttons are never covered.
  // If the page is shorter than the zone, expansion starts from scroll=0 (short pages).
  // Height is written directly to the DOM — never via JSX style — so React
  // re-renders triggered by setIsExpanded cannot reset it.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const root = document.getElementById('root');
    const panel = panelRef.current;
    if (!root || !panel) return undefined;
    let rafId = null;

    const update = () => {
      const expandedPx = getExpandedPx();
      const zoneH = Math.max(0, expandedPx - collapsedHeight);
      const { scrollTop, scrollHeight, clientHeight } = root;
      const maxScroll = scrollHeight - clientHeight;

      // Expansion zone = last zoneH px of scroll. zoneStart shifts to 0 for short pages.
      const zoneStart = Math.max(0, maxScroll - zoneH);
      const zoneRange = maxScroll - zoneStart;

      const progress = zoneRange > 0
        ? Math.max(0, Math.min(1, (scrollTop - zoneStart) / zoneRange))
        : 0;

      const newHeight = collapsedHeight + progress * zoneH;
      panel.style.height = `${newHeight}px`;

      const nowExpanded = progress > 0.5;
      if (nowExpanded !== expandedRef.current) {
        expandedRef.current = nowExpanded;
        setIsExpanded(nowExpanded);
      }
      // Spatial trigger: hand scroll off to the ISI content once the panel
      // physically fills the viewport (within 2px). Using panel height avoids
      // the sub-pixel scrollTop precision issues that prevent progress === 1.
      const nowFull = zoneH > 0 && newHeight >= expandedPx - 2;
      if (nowFull !== isFullyExpandedRef.current) {
        isFullyExpandedRef.current = nowFull;
        setIsFullyExpanded(nowFull);
        // Set overflowY directly here (same frame as the ref update) so wheel/touch
        // handlers can route to the content on the very next event — before React's
        // queued re-render has flushed the style update.
        const contentEl = document.getElementById('scrollable-isi-content');
        if (contentEl) contentEl.style.overflowY = nowFull ? 'auto' : 'hidden';
      }
    };

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      root.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [enabled, collapsedHeight, getExpandedPx]);

  // Wheel forwarder: position:fixed elements are outside the normal scroll tree,
  // so desktop wheel events over the ISI may not reach #root. Capture them here
  // and apply the delta directly so the user can scroll up to collapse the ISI.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const footer = footerRef.current;
    if (!footer) return undefined;
    const root = document.getElementById('root');
    if (!root) return undefined;

    const onWheel = (e) => {
      // Use root scroll position as the routing trigger, not just isFullyExpandedRef,
      // because the ref is updated in a rAF callback that fires one frame after the
      // last root scroll event — wheel events can arrive before that rAF fires.
      const rootAtMax = root.scrollTop >= root.scrollHeight - root.clientHeight - 2;

      if (isFullyExpandedRef.current || rootAtMax) {
        const content = document.getElementById('scrollable-isi-content');
        if (content) {
          // overflowY may still be 'hidden' if the rAF hasn't run yet — set eagerly.
          if (content.style.overflowY !== 'auto') content.style.overflowY = 'auto';
          const atBottom = e.deltaY > 0 && content.scrollTop >= content.scrollHeight - content.clientHeight - 2;
          const atTop = e.deltaY < 0 && content.scrollTop <= 0;
          if (!atBottom && !atTop) {
            e.preventDefault();
            content.scrollTop += e.deltaY;
            return;
          }
        }
      }

      e.preventDefault();
      root.scrollTop += e.deltaY;
    };

    footer.addEventListener('wheel', onWheel, { passive: false });
    return () => footer.removeEventListener('wheel', onWheel);
  }, [enabled]);

  // Touch forwarder: mirrors the wheel forwarder for mobile.
  // Fixed elements are outside the normal scroll tree, so swipe gestures on the
  // ISI don't reach #root. We capture touchmove and route it ourselves.
  // Routing is based on root scroll position (synchronous), not isFullyExpandedRef,
  // to avoid the one-frame gap between the last root scroll event and the rAF.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const footer = footerRef.current;
    if (!footer) return undefined;
    const root = document.getElementById('root');
    if (!root) return undefined;

    let lastTouchY = 0;

    const onTouchStart = (e) => {
      lastTouchY = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      const clientY = e.touches[0].clientY;
      const deltaY = lastTouchY - clientY; // positive = finger moved up = scroll down
      lastTouchY = clientY;

      // Use root scroll position as the routing trigger, not just isFullyExpandedRef,
      // because the ref is updated in a rAF that fires one frame after the last root
      // scroll event — touch events in the same gesture can arrive before that rAF.
      const rootAtMax = root.scrollTop >= root.scrollHeight - root.clientHeight - 2;

      if (isFullyExpandedRef.current || rootAtMax) {
        const content = document.getElementById('scrollable-isi-content');
        if (content) {
          // overflowY may still be 'hidden' if the rAF hasn't run yet — set eagerly.
          if (content.style.overflowY !== 'auto') content.style.overflowY = 'auto';
          const atBottom = deltaY > 0 && content.scrollTop >= content.scrollHeight - content.clientHeight - 2;
          const atTop = deltaY < 0 && content.scrollTop <= 0;
          if (!atBottom && !atTop) {
            // Explicitly scroll ISI content — returning without preventDefault is
            // unreliable for fixed elements; once preventDefault() was called earlier
            // in this gesture (during expansion), iOS/Android won't re-enable native
            // scroll for the remainder of the gesture.
            e.preventDefault();
            content.scrollTop += deltaY;
            return;
          }
        }
      }

      e.preventDefault();
      root.scrollTop += deltaY;
    };

    footer.addEventListener('touchstart', onTouchStart, { passive: true });
    footer.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      footer.removeEventListener('touchstart', onTouchStart);
      footer.removeEventListener('touchmove', onTouchMove);
    };
  }, [enabled]);

  // Toggle: expand → scroll to page bottom (ISI fully open).
  // Collapse → scroll just before the expansion zone so ISI returns to collapsedHeight.
  const toggle = React.useCallback(() => {
    const root = document.getElementById('root');
    if (!root) return;
    const expandedPx = getExpandedPx();
    const zoneH = Math.max(0, expandedPx - collapsedHeight);
    const maxScroll = root.scrollHeight - root.clientHeight;
    const zoneStart = Math.max(0, maxScroll - zoneH);
    if (expandedRef.current) {
      const content = document.getElementById('scrollable-isi-content');
      if (content) content.scrollTop = 0;
    }
    root.scrollTo({
      top: expandedRef.current ? Math.max(0, zoneStart - 10) : maxScroll,
      behavior: 'smooth',
    });
  }, [collapsedHeight, getExpandedPx]);

  // Spacer = expandedPx + footerHeight + spacerExtra so all page content above the
  // spacer clears the fully-expanded ISI panel — submit/cancel buttons stay visible.
  const spacerHeightPx = getExpandedPx() + footerHeight + spacerExtra;

  return { panelRef, footerRef, isExpanded, isFullyExpanded, toggle, spacerHeightPx };
};

// Toggle-only mode: discrete expand/collapse via button click with a CSS height transition.
const useToggleOnly = ({ enabled, collapsedHeight, expandedHeight, footerHeight, spacerExtra, defaultExpanded, showStandardFooter }) => {
  const [isExpanded, setIsExpanded] = React.useState(Boolean(defaultExpanded));

  // Reset ISI scroll position when collapsing so it starts from the top on re-expand.
  React.useEffect(() => {
    if (!enabled || isExpanded) return;
    const el = document.getElementById('scrollable-isi-content');
    if (el) el.scrollTop = 0;
  }, [enabled, isExpanded]);

  const toggle = React.useCallback(() => setIsExpanded((p) => !p), []);

  // 'screen' mode fills the viewport via flex layout rather than a fixed height.
  const useFullscreen = expandedHeight === 'screen' && isExpanded;
  const normalizeH = (h) => (typeof h === 'number' ? `${h}px` : h);
  const panelStyle = useFullscreen
    ? { flex: 1, minHeight: 0 }
    : { height: normalizeH(isExpanded ? expandedHeight : collapsedHeight), transition: 'height 180ms ease' };
  const containerStyle = useFullscreen ? { top: 0, display: 'flex', flexDirection: 'column' } : {};
  const spacerHeight = showStandardFooter
    ? `calc(${collapsedHeight}px + ${footerHeight}px + ${spacerExtra}px)`
    : `calc(${collapsedHeight}px + ${spacerExtra}px)`;

  return { isExpanded, toggle, panelStyle, containerStyle, spacerHeight };
};

// companyFooter – JSX rendered in the footer bar below the ISI panel.
// config       – all configuration options (see inline comments below).
// context      – arbitrary object passed through to function-based content (e.g. tenant, meetingInfo).
const ScrollableIsiFooter = ({ companyFooter, config, context }) => {
  const {
    // Accessible name for the ISI panel <section> region.
    ariaLabel = 'Important safety information',
    // Content shown when collapsed. Also shown at the top of the expanded view when
    // includeCollapsedInExpanded is true. Accepts JSX, a string, an array, or a
    // render function receiving { isExpanded, toggle, ...context }.
    collapsedContent,
    // Additional content shown only in the expanded view.
    expandedContent,
    // When true (default), collapsedContent is prepended to expandedContent on expand.
    includeCollapsedInExpanded = true,
    // Collapsed panel height in pixels.
    collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
    // Fully-expanded panel height. Accepts a pixel number, a CSS string ('70vh', '400px', '50%'),
    // or 'screen' to fill the viewport via flex layout (toggle-only mode only).
    expandedHeight = '70vh',
    // Height in pixels of the company footer bar rendered below the ISI panel.
    // Ignored when footerOnlyWhenExpanded is true.
    footerHeight = 40,
    // Extra pixels added to the bottom spacer so page content clears the fully-expanded panel.
    spacerExtra = 0,
    // Additional CSS class on the outer footer element.
    className = '',
    // Inline style overrides for the outer footer element.
    style = {},
    // Inline style overrides for the header bar containing the toggle button.
    headerStyle = {},
    // Inline style overrides for the scrollable ISI content area.
    contentStyle = {},
    // Inline style overrides for the company footer bar.
    footerStyle = {},
    // Toggle button labels used when expandButton/collapseButton are not provided.
    expandLabel = 'Show more',
    collapseLabel = 'Show less',
    // Custom JSX for the expand button (replaces the default +/label content).
    expandButton,
    // Custom JSX for the collapse button (replaces the default -/label content).
    collapseButton,
    // When true, the toggle button is rendered in a full-width overlay div rather than a header bar.
    hideHeader = false,
    // When true, panel height is driven by page scroll position (progressive expansion)
    // rather than discrete toggle clicks.
    scrollLinked = false,
    // When true, renders the company footer below the ISI panel.
    showStandardFooter = true,
    // scroll-linked only: move the company footer inside the ISI content area so it only
    // appears once the panel is fully expanded. The panel expands to viewport height rather
    // than leaving room for an external footer bar.
    footerOnlyWhenExpanded = false,
    // toggle-only only: start in the expanded state on first render.
    defaultExpanded = false,
  } = config;

  // When the footer lives inside the scroll content, no external footer height needs to be
  // reserved in the spacer or expansion zone calculations.
  const effectiveFooterHeight = footerOnlyWhenExpanded ? 0 : footerHeight;

  const sl = useScrollLinked({ enabled: scrollLinked, collapsedHeight, expandedHeight, footerHeight: effectiveFooterHeight, spacerExtra, footerOnlyWhenExpanded });
  const tg = useToggleOnly({ enabled: !scrollLinked, collapsedHeight, expandedHeight, footerHeight: effectiveFooterHeight, spacerExtra, defaultExpanded, showStandardFooter });

  const isExpanded = scrollLinked ? sl.isExpanded : tg.isExpanded;
  const toggle = scrollLinked ? sl.toggle : tg.toggle;
  const mergedContext = { ...context, isExpanded, toggle };

  const expandedBody = includeCollapsedInExpanded ? (
    <>{renderContent(collapsedContent, mergedContext)}{renderContent(expandedContent, mergedContext)}</>
  ) : renderContent(expandedContent, mergedContext);

  const toggleButton = (
    <button
      aria-controls="scrollable-isi-content"
      aria-expanded={isExpanded}
      className="scrollable-isi-toggle"
      onClick={toggle}
      type="button"
    >
      {isExpanded
        ? (collapseButton !== undefined ? collapseButton : <><span aria-hidden="true">-</span>{collapseLabel}</>)
        : (expandButton !== undefined ? expandButton : <><span aria-hidden="true">+</span>{expandLabel}</>)
      }
    </button>
  );

  return (
    <>
      <div
        className="scrollable-isi-spacer"
        style={{ height: scrollLinked ? sl.spacerHeightPx : tg.spacerHeight }}
      />
      <div
        ref={scrollLinked ? sl.footerRef : undefined}
        className={`scrollable-isi-footer ${isExpanded ? 'expanded' : 'collapsed'} ${className}`}
        style={{ ...style, overflowX: 'hidden', ...(scrollLinked ? {} : tg.containerStyle) }}
      >
        <section
          ref={scrollLinked ? sl.panelRef : undefined}
          aria-label={ariaLabel}
          className="scrollable-isi-panel"
          style={scrollLinked ? undefined : tg.panelStyle}
        >
          {hideHeader ? (
            <div className="scrollable-isi-toggle-overlay">{toggleButton}</div>
          ) : (
            <div className="scrollable-isi-header" style={headerStyle}>{toggleButton}</div>
          )}
          <div
            id="scrollable-isi-content"
            className="scrollable-isi-content"
            style={scrollLinked
              ? {
                  ...contentStyle,
                  overflowY: sl.isFullyExpanded ? 'auto' : 'hidden',
                  ...(footerOnlyWhenExpanded ? { paddingLeft: 0, paddingRight: 0, paddingBottom: 0 } : {}),
                }
              : contentStyle}
          >
            {scrollLinked
              ? expandedBody
              : (isExpanded ? expandedBody : renderContent(collapsedContent, mergedContext))
            }
            {showStandardFooter && footerOnlyWhenExpanded && (
              <div className="footer scrollable-isi-standard-footer" style={footerStyle}>
                {companyFooter}
              </div>
            )}
          </div>
        </section>
        {showStandardFooter && !footerOnlyWhenExpanded && (
          <div className="footer scrollable-isi-standard-footer" style={footerStyle}>
            {companyFooter}
          </div>
        )}
      </div>
    </>
  );
};

export default ScrollableIsiFooter;
